import { useTheme } from '@emotion/react';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import {
  AddPatientPreference,
  CreatePatientFormData,
  InvitePatientFormData,
  PatientContextAction,
  PatientContextValues,
  PatientDemographicInfoPayload,
  PatientForm,
  PatientInvitesPayload,
  UserMetadata,
  UserMetadataFlags,
} from 'pages/Dashboard/types';
import Spinner from 'components/Common/Spinner';
import {
  PatientContext,
  patientContextInitialState,
  patientFormInitialState,
  patientReducer,
} from 'pages/Dashboard/AddPatientContext';
import PageHeader from 'components/Common/PageHeader';
import { useMutation, useQuery } from 'react-query';
import { setCreatePatientPayload, setInvitePatientsPayload } from 'utils/service';
import { useHttp } from 'hooks/use-fetch';
import Result from 'pages/Dashboard/types/mixpanel.types';
import { ToastType } from 'components/Common/Toast';
import { createPatient, invitePatients } from 'pages/Dashboard/services/patient.services';
import AddNewPatientForm from 'pages/Dashboard/AddNewPatientForm';
import AddNewPatientSuccess from 'pages/Dashboard/components/AddNewPatientSuccess';
import { providers } from 'utils/apiUrls';
import { fetchUserMetadata, postUserMetadata } from 'pages/Dashboard/services/provider.services';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

export default function AddPatient() {
  const [showBulkUploadSuccess, setShowBulkUploadSuccess] = React.useState(false);
  const theme = useTheme();
  const { http, providerId, setToastProps } = useHttp();
  const [patientFormDemographic, setPatientFormDemographic] = useState<boolean>(false);
  const [addPatientTime, setAddPatientTime] = React.useState<string>(new Date().toISOString());
  const [addPatientWithoutInvite, setAddPatientWithoutInvite] = React.useState<boolean>(false);
  const [isMinor, setIsMinor] = React.useState<boolean>(false);
  const [state, dispatch] = React.useReducer(patientReducer, patientContextInitialState);

  const formMethods = useForm<PatientForm>({
    defaultValues: patientFormInitialState,
  });

  const { isFetching: isPreferenceFetching, refetch } = useQuery(providers.metadata.queryUrl, {
    queryFn: fetchUserMetadata(http.post, {
      name: UserMetadataFlags.AddPatientPreference,
      userId: providerId,
    }),
    onSuccess: (data) => {
      if (data.flag) {
        const value = data.flag?.value;
        if (value === AddPatientPreference.Demographic) {
          setPatientFormDemographic(true);
        }
      }
    },
    onError: () => {
      setPatientFormDemographic(false);
    },
    retry: false,
  });

  const {
    isFetching: isFetchingPatientWithoutInviteFlag,
  } = useQuery(
    [providers.metadata.queryUrl, UserMetadataFlags.AddPatientWithoutInvite.toString()],
    {
      queryFn: fetchUserMetadata(http.post, {
        name: UserMetadataFlags.AddPatientWithoutInvite,
        userId: providerId,
      }),
      onSuccess: (data) => {
        if (data.flag) {
          const value = data.flag?.value;
          setAddPatientWithoutInvite(value === 'true');
        }
      },
      onError: () => {
        setAddPatientWithoutInvite(false);
      },
      retry: false,
    },
  );

  const setUserMetadataMutation = useMutation({
    mutationFn: (payload: UserMetadata) => postUserMetadata(http.post, { ...payload }),
  });

  const createPatientMutation = useMutation({
    // eslint-disable-next-line max-len
    mutationFn: (payload: PatientDemographicInfoPayload) => createPatient(http.post, providerId, payload),
    onSuccess: (data, variables) => {
      const eventPayload = {
        'datetime requested': addPatientTime,
        'datetime completed': new Date().toISOString(),
        result: data?.totalFailed === 0 ? Result.SUCCESS : Result.FAIL,
      };

      trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_HEALTH_RECORDS_REQUESTED, eventPayload);

      if (data?.totalFailed === 0) {
        const sendInviteEventPayload = {
          datetime_requested: addPatientTime,
          datetime_completed: new Date().toISOString(),
          added_phone_number: !!variables?.patient?.phoneNumber?.number,
          added_email: !!variables?.patient?.email,
          create_without_invitation_flag: !!variables?.createWithoutInvitation,
          demographic_flow: true,
          isLessThan13: isMinor,
        };
        trackMixpanelEvent(
          MixpanelEventName.ADD_PATIENT_SEND_INVITE_SUCCESS,
          sendInviteEventPayload,
        );
        setToastProps({
          message: 'Patient has been successfully invited.',
          open: true,
          type: ToastType.success,
        });
      }
      else {
        dispatch({
          type: PatientContextAction.SET_PATIENT_INVITE_ERROR_LIST,
          payload: { inviteErrors: data?.inviteErrors ?? {} },
        });
      }
    },
    onError: (error) => {
      trackMixpanelEvent(
        MixpanelEventName.ADD_PATIENT_SEND_INVITE_ERROR,
        { errorMessage: (error as Error)?.message },
      );
      setToastProps({
        message: (error as Error)?.message,
        open: true,
        type: ToastType.error,
      });
    },
  });

  const invitePatientsMutation = useMutation({
    mutationFn: (payload: PatientInvitesPayload) => invitePatients(http.post, providerId, payload),
    onSuccess: (data, variables) => {
      formMethods.clearErrors();
      if (data?.totalFailed === 0) {
        const sendInviteEventPayload = {
          datetime_requested: addPatientTime,
          datetime_completed: new Date().toISOString(),
          demographic_flow: false,
          added_email: variables?.patient?.map((p) => !!p.email).toString(),
          number_of_patients: variables?.patient?.length ?? 0,
        };
        trackMixpanelEvent(
          MixpanelEventName.ADD_PATIENT_SEND_INVITE_SUCCESS,
          sendInviteEventPayload,
        );

        setToastProps({
          message: 'Patient(s) have been successfully invited.',
          open: true,
          type: ToastType.success,
        });
      }
      else {
        dispatch({
          type: PatientContextAction.SET_PATIENT_INVITE_ERROR_LIST,
          payload: { inviteErrors: data?.inviteErrors ?? {} },
        });
      }
    },
    onError: (error) => {
      trackMixpanelEvent(
        MixpanelEventName.ADD_PATIENT_SEND_INVITE_ERROR,
        { errorMessage: (error as Error)?.message },
      );
      setToastProps({
        message: (error as Error)?.message,
        open: true,
        type: ToastType.error,
      });
    },
  });

  const onSubmit: SubmitHandler<PatientForm> = (data, event) => {
    const submitter = (event?.nativeEvent as SubmitEvent | undefined)?.submitter as
      | HTMLButtonElement
      | undefined;
    const buttonLabel = submitter?.innerText
      || submitter?.value
      || '';

    if (buttonLabel.toLowerCase() !== 'resend invite') {
      setAddPatientTime(new Date().toISOString());

      if (patientFormDemographic) {
        const payload = setCreatePatientPayload(
          data as CreatePatientFormData,
          addPatientWithoutInvite,
          isMinor,
        );
        createPatientMutation.mutate(payload);
      }
      else {
        const payload = setInvitePatientsPayload(data as InvitePatientFormData);
        invitePatientsMutation.mutate(payload);
      }
    }
  };

  const value: PatientContextValues = React.useMemo(
    () => ({
      addingPatient: createPatientMutation.isLoading || invitePatientsMutation.isLoading,
      dispatch,
      state,
      practicesList: null,
    }),
    [createPatientMutation.isLoading, invitePatientsMutation.isLoading, state],
  );

  const invitePatientsSuccess = invitePatientsMutation?.data?.totalFailed === 0;
  const createPatientSuccess = createPatientMutation?.data?.totalFailed === 0;

  const setAddPatientPreference = (val: boolean) => {
    setUserMetadataMutation.mutate({
      userId: providerId,
      flag: {
        name: UserMetadataFlags.AddPatientPreference,
        value: val ? AddPatientPreference.Demographic : AddPatientPreference.Invite,
      },
    });
    if (val) {
      formMethods.unregister('patients');
      formMethods.clearErrors();
    }
    else {
      formMethods.register('patients', { value: [{ email: '', mobile: '', firstName: '', lastName: '' }] });
    }
    setPatientFormDemographic(val);
    dispatch({
      type: PatientContextAction.SET_PATIENT_INVITE_ERROR_LIST,
      payload: { inviteErrors: {} },
    });
  };

  const createInvitePatientsSuccess = createPatientSuccess || invitePatientsSuccess;

  if (isPreferenceFetching || isFetchingPatientWithoutInviteFlag) {
    return <Spinner />;
  }

  let content: React.ReactNode;

  if (showBulkUploadSuccess) {
    content = (
      <AddNewPatientSuccess
        patientFormDemographic={false}
        addedPatientWithoutInvite={false}
        isBulkUpload
        onAddAnotherPatientClick={() => {
          setShowBulkUploadSuccess(false);
          formMethods.reset(patientFormInitialState);
          invitePatientsMutation.reset();
          createPatientMutation.reset();
          refetch();
          setPatientFormDemographic(false);
        }}
      />
    );
  }
  else if (createInvitePatientsSuccess) {
    content = (
      <AddNewPatientSuccess
        patientFormDemographic={patientFormDemographic}
        addedPatientWithoutInvite={!!createPatientMutation?.variables?.createWithoutInvitation}
        onAddAnotherPatientClick={() => {
          formMethods.reset(patientFormInitialState);
          invitePatientsMutation.reset();
          createPatientMutation.reset();
          refetch();
          setPatientFormDemographic(false);
        }}
      />
    );
  }
  else {
    content = (
      <Box sx={{ margin: { sm: theme.spacing(3) } }}>
        <PageHeader title='Add a patient' titleVariant='h4' />
        <FormProvider {...formMethods}>
          <AddNewPatientForm
            onSubmit={onSubmit}
            patientFormDemographic={patientFormDemographic}
            addPatientWithoutInvite={addPatientWithoutInvite}
            setIsMinor={setIsMinor}
            setPatientFormDemographic={setAddPatientPreference}
            isLoading={
              createPatientMutation.isLoading || invitePatientsMutation.isLoading
            }
            onSuccess={() => setShowBulkUploadSuccess(true)}
          />
        </FormProvider>
      </Box>
    );
  }

  return (
    <PatientContext.Provider value={value}>
      {content}
    </PatientContext.Provider>
  );
}
